import { WhiteBox, FoodsBadge, Banner } from "@foodpilot/foods";
import { Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ValidatedTag } from "../../ValidatedTag";
import { DiagnosticProps } from "./diagnostic.types";
import { DIAGNOSTIC_COLOR_INDEX } from ".";
import { EmptyDiagnostic } from "./Exceptions/EmptyDiagnostic";
import { DiagnosticInner } from "./Content/DiagnosticInner";
import { RegulationDiagnostic } from "./Content/RegulationDiagnostic";
import { NotEnoughData } from "./Exceptions/NotEnoughData";

export const Diagnostic = (props: DiagnosticProps) => {
    const { diagnostic, diagnosticTitle, diagnosticSubtitle, score } = props;

    const theme = useTheme();
    const { t } = useTranslation();
    const datavizColors = theme.custom.dataviz.colorsWithExtra[DIAGNOSTIC_COLOR_INDEX];

    const isRegulatory = score.displayMode === "regulation";

    const isQuestionnaireStarted = (diagnostic?.post?.completion ?? 0) > 0;
    const isPostEmpty = !diagnostic?.post;
    const isEmpty = diagnostic === null || isPostEmpty || isQuestionnaireStarted === false;
    const isScoreNull = diagnostic?.post?.scores?.score.value === null;

    return (
        <WhiteBox border={`2px solid ${datavizColors.main}`}>
            <Stack padding={{ xs: theme.spacing(2), sm: theme.spacing(3) }} gap={theme.spacing(3)}>
                <Stack
                    flexDirection={"row"}
                    gap={theme.spacing(2)}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                >
                    <Stack flexDirection="row" gap={theme.spacing(2)} alignItems={"center"}>
                        <FoodsBadge
                            size={8}
                            icon={isRegulatory ? "diagnostic" : "questionnaireAnswer"}
                            boxProps={{
                                sx: {
                                    color: datavizColors.variant[700],
                                    backgroundColor: datavizColors.variant[100],
                                    borderRadius: "8px",
                                },
                            }}
                        />
                        <Stack>
                            <Typography variant="h2">{diagnosticTitle}</Typography>
                            {diagnosticSubtitle && <Typography variant="body">{diagnosticSubtitle}</Typography>}
                        </Stack>
                    </Stack>
                    <Stack flexDirection={"row"} gap={theme.spacing(2)} alignItems={"center"}>
                        <ValidatedTag state={props.validatedState} />
                    </Stack>
                </Stack>
                {props.validatedState === "incomplete" && (
                    <Banner variant="error">
                        {t(
                            "company.dashboard.not_enough_data",
                            "The score displayed is not reliable because some data is missing. Please complete the questionnaire to obtain a reliable result.",
                        )}
                    </Banner>
                )}
                {isEmpty ?
                    <EmptyDiagnostic {...props} />
                : isScoreNull ?
                    <NotEnoughData {...props} />
                : isRegulatory ?
                    <RegulationDiagnostic {...props} />
                :   <DiagnosticInner {...props} />}
            </Stack>
        </WhiteBox>
    );
};
